import * as React from 'react';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { updateOnboardingDto } from './sagas/pmonboardingSlice';
import { Box, Button, Card, CardContent, Grid, Link, List, ListItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { FeeTypeEnum, PaymentProductOption, TieredCompanyPricingOptionDTO, AmountTier, TieredFeeTypeEnum, PaymentTypeEnum } from '@inosio/payments-sdk';
import { useHistory } from 'react-router';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import { selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface PricingCardPropTypes {
  contract: PaymentProductOption;
  selectedGuid: string;
  setSelectedGuid: React.Dispatch<React.SetStateAction<string>>;
  isUpdateMode?: boolean;
  disabledUpdateButton?: boolean;
  actionButtonText?: string;
  isFetchingData?: boolean;
  errorMessage?: string | null;
}

interface TieredPricingTablePropTypes {
  tiers: AmountTier[];
  PaymentType?: PaymentTypeEnum;
}

export default function PricingCard(props: PricingCardPropTypes) {
  const [showPricingTable, setShowPricingTable] = React.useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { selectedPropertyGroupAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);

  const [pmAch, pmDb, pmCC] = props.contract?.PaidByPM ?? [];
  const [resAch, resDb, resCC] = props.contract?.PaidByPayer ?? [];

  //TODO: @hussain Fix this component or remove it if needed
  const isPmNoCost = (pmAch as any)?.Amount === 0 && (pmDb as any)?.Amount === 0 && (pmCC as any).Amount === 0;
  const isResFree = (resAch as any)?.Amount === 0 && (resDb as any)?.Amount === 0 && (resCC as any).Amount === 0;
  const isPmOneDollarAch = (pmAch as any)?.Amount === 100 && (pmDb as any)?.Amount === 0 && (pmCC as any).Amount === 0;

  const isPMTiered = pmDb?.FeeType === FeeTypeEnum.T;
  const isResFullyTiered: boolean = (resAch as any)?.Amount !== 0 && resDb?.FeeType === FeeTypeEnum.T;
  const isResPartialTiered: boolean = (resAch as any)?.Amount === 0 && resDb?.FeeType === FeeTypeEnum.T;
  const default_tiers: AmountTier[] =
    isPmNoCost || (isPmOneDollarAch && !isPMTiered)
      ? (props.contract?.PaidByPayer?.filter((pmt) => pmt.FeeType === FeeTypeEnum.T)?.[0] as TieredCompanyPricingOptionDTO)?.AmountTiers
      : (props.contract?.PaidByPM?.filter((pmt) => pmt.FeeType === FeeTypeEnum.T)?.[0] as TieredCompanyPricingOptionDTO)?.AmountTiers;

  React.useEffect(() => {
    props.selectedGuid &&
      dispatch({
        type: updateOnboardingDto.type,
        payload: {
          RateOptionUuid: props.selectedGuid,
        },
      });
  }, [props.selectedGuid]);

  const handleClickSelectGuid = () => {
    props.setSelectedGuid && props.setSelectedGuid(props.contract?.Uuid ?? '');
  };

  const handleClickUpdatePlan = () => {
    history.push(`/pricing?isUpdateMode=true`);
  };

  return (
    <Card
      sx={{
        maxWidth: '400px',
        height: '450px',
        borderRadius: '25px',
        marginTop: '5%',
        marginRight: '5%',
        position: 'relative',
        border: props.selectedGuid === props.contract?.Uuid && !props.isUpdateMode ? '4px solid #39A2DB' : undefined,
      }}
    >
      {showPricingTable && !!props.contract.Uuid && (
        <Box sx={{ width: '210px', height: '150px', position: 'absolute', top: '35%', left: '20%', zIndex: '9', background: '#ffffff', border: '2px solid #eeeeee', borderRadius: '5px' }}>
          <TieredPricingTable tiers={default_tiers} />
        </Box>
      )}
      <CardContent sx={{ padding: '0', height: '100%', fontFamily: 'Work Sans' }}>
        <Box sx={{ height: '60px', display: 'flex', flexDirection: 'column', background: '#eeeeee' }}>
          {!!props.isUpdateMode && (
            <Typography
              sx={{
                fontWeight: '700',
                fontSize: props.errorMessage || props.isFetchingData ? '16px' : '14px',
                fontFamily: 'Work Sans',
                lineHeight: '20px',
                paddingLeft: '5%',
                paddingTop: props.errorMessage || props.isFetchingData ? '5%' : '2%',
                color: '#6B7280',
              }}
            >
              Selected Pricing Plan
            </Typography>
          )}
          {!props.errorMessage && !props.isFetchingData && (
            <Typography sx={{ lineHeight: '20px', marginTop: 'auto', marginBottom: '8px', paddingLeft: '5%', fontWeight: '700', color: '#39A2DB' }}>
              {isPmNoCost && !selectedPropertyGroupAssignedConfigs.MaintenanceProductPurchased && !selectedPropertyGroupAssignedConfigs.ApplicationsProductPurchased
                ? 'No Cost'
                : isPmOneDollarAch || selectedPropertyGroupAssignedConfigs.MaintenanceProductPurchased || selectedPropertyGroupAssignedConfigs.ApplicationsProductPurchased
                ? 'Some Cost'
                : props.actionButtonText
                ? 'Custom'
                : 'Go Digital'}
            </Typography>
          )}
        </Box>

        {props.isUpdateMode && props.isFetchingData ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            style={{
              width: '100%',
              height: '75%',
            }}
          >
            <SpinnerWithNoConfirmation />
          </Stack>
        ) : props.isUpdateMode && props.errorMessage ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            style={{
              width: '100%',
              height: '75%',
            }}
          >
            <Typography
              style={{
                fontFamily: 'Work Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '18px',
                color: '#DB3939',
                padding: '5%',
                textAlign: 'center',
              }}
            >
              Unable to fetch pricing plan. Please try again later or contact support.
            </Typography>
          </Stack>
        ) : !!props.contract.Uuid && !props.actionButtonText ? (
          <>
            <Stack direction={'column'}>
              <Typography sx={{ padding: '0 5%', paddingRight: '0', marginTop: '16px', fontWeight: '700' }}>Paid By You</Typography>
              <Box
                sx={{
                  paddingTop: !isPmNoCost && !isPmOneDollarAch ? '2%' : undefined,
                  width: !isPmNoCost && !isPmOneDollarAch ? '100%' : '70%',
                  height: '160px',
                  alignSelf: 'center',
                  borderBottom: '1px solid #eeeeee',
                  boxSizing: 'border-box',
                }}
              >
                {isPmNoCost ? (
                  !(selectedPropertyGroupAssignedConfigs.MaintenanceProductPurchased || selectedPropertyGroupAssignedConfigs.ApplicationsProductPurchased) ? (
                    <Typography
                      sx={{
                        lineHeight: '160px',
                        fontSize: '130px',
                        fontWeight: '700',
                        textAlign: 'center',
                        color: '#6B7280',
                      }}
                    >
                      $0
                    </Typography>
                  ) : (
                    <Box sx={{ maxWidth: '400px' }}>
                      <List
                        sx={{
                          listStyleType: 'disc',
                          ml: 6,
                          '& .MuiListItem-root': {
                            display: 'list-item',
                          },
                        }}
                      >
                        {selectedPropertyGroupAssignedConfigs.MaintenanceProductPurchased && selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee && (
                          <ListItem>
                            <Typography sx={{ color: 'black', fontWeight: '400', fontFamily: 'Work Sans', fontSize: '16px' }}>
                              {Number.isInteger(selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100)
                                ? `$${selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100}/month, Unlimited Maintenance Requests`
                                : `$${(selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100).toFixed(2)}/month, Unlimited Maintenance Requests`}
                            </Typography>
                          </ListItem>
                        )}
                        {selectedPropertyGroupAssignedConfigs.ApplicationsProductPurchased && (
                          <ListItem>
                            <Typography sx={{ color: 'black', fontWeight: '400', fontFamily: 'Work Sans', fontSize: '16px' }}>{`$${
                              (selectedPropertyGroupAssignedConfigs?.ApplicationsProductOccurrenceAmount ?? 0) / 100
                            } per Applicant`}</Typography>
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )
                ) : (
                  <>
                    {isPmOneDollarAch ? (
                      <Typography
                        sx={{
                          lineHeight: '160px',
                          fontSize: '130px',
                          fontWeight: '700',
                          textAlign: 'center',
                          color: '#6B7280',
                        }}
                      >
                        $1<span style={{ lineHeight: '30px', fontSize: '20px', fontWeight: '400', marginLeft: '-10px' }}> per ACH</span>
                      </Typography>
                    ) : (
                      // pm pays everything - Go Digital
                      <>
                        <Box sx={{ maxWidth: '400px' }}>
                          <List
                            sx={{
                              listStyleType: 'disc',
                              ml: 6,
                              '& .MuiListItem-root': {
                                display: 'list-item',
                              },
                            }}
                          >
                            {selectedPropertyGroupAssignedConfigs.MaintenanceProductPurchased && selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee && (
                              <ListItem>
                                <Typography sx={{ color: 'black', fontWeight: '400', fontFamily: 'Work Sans', fontSize: '16px' }}>
                                  {Number.isInteger(selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100)
                                    ? `$${selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100}/month, Unlimited Maintenance Requests`
                                    : `$${(selectedPropertyGroupAssignedConfigs?.MaintenanceProductBaseFee / 100).toFixed(2)}/month, Unlimited Maintenance Requests`}
                                </Typography>
                              </ListItem>
                            )}
                            {selectedPropertyGroupAssignedConfigs.ApplicationsProductPurchased && (
                              <ListItem>
                                <Typography sx={{ color: 'black', fontWeight: '400', fontFamily: 'Work Sans', fontSize: '16px' }}>{`$${
                                  (selectedPropertyGroupAssignedConfigs?.ApplicationsProductOccurrenceAmount ?? 0) / 100
                                } per Applicant`}</Typography>
                              </ListItem>
                            )}
                            {selectedPropertyGroupAssignedConfigs.PaymentsProductPurchased && (pmAch as any).Amount && (
                              <ListItem>
                                <Typography sx={{ color: 'black', fontWeight: '400', fontFamily: 'Work Sans', fontSize: '16px' }}>
                                  {Number.isInteger((pmAch as any).Amount / 100) ? `$${(pmAch as any).Amount / 100} per ACH` : `$${((pmAch as any).Amount / 100).toFixed(2)} per ACH`}
                                </Typography>
                              </ListItem>
                            )}
                          </List>
                        </Box>
                        {/* <RowOfFees setShowPricingTable={isPMTiered ? setShowPricingTable : undefined} tiers={isPMTiered ? (pmDb as TieredCompanyPricingOptionDTO).AmountTiers : undefined} dbPricing={(pmDb as CompanyPricingOptionDTO).Amount} achPricing={(pmAch as CompanyPricingOptionDTO).Amount} ccPricing={(pmCC as CompanyPricingOptionDTO).Amount} /> */}
                      </>
                    )}
                  </>
                )}
                {}
              </Box>
              <Typography sx={{ padding: '0 5%', paddingRight: '0', marginTop: '8px', fontWeight: '700' }}>Paid By Resident</Typography>
            </Stack>
            {/* Resident Area */}
            {isResFree ? (
              <Typography
                sx={{
                  lineHeight: '80px',
                  fontSize: '70px',
                  textAlign: 'center',
                  color: '#6B7280',
                  margin: '2% 0',
                }}
              >
                Free
              </Typography>
            ) : (
              <>
                {isPmOneDollarAch ? (
                  <RowOfFees
                    setShowPricingTable={isResPartialTiered ? setShowPricingTable : undefined}
                    tiers={isResPartialTiered ? (resDb as TieredCompanyPricingOptionDTO).AmountTiers : undefined}
                    dbPricing={(resDb as any).Amount}
                    achPricing={(resAch as any).Amount}
                    ccPricing={(resCC as any).Amount}
                    isPartial={true}
                  />
                ) : (
                  <>
                    <RowOfFees
                      setShowPricingTable={isResFullyTiered ? setShowPricingTable : undefined}
                      tiers={isResFullyTiered ? (resDb as TieredCompanyPricingOptionDTO).AmountTiers : undefined}
                      dbPricing={(resDb as any).Amount}
                      achPricing={(resAch as any).Amount}
                      ccPricing={(resCC as any).Amount}
                    />
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Typography
              sx={{
                pt: 10,
                pb: 12,
                color: '#6B7280',
                textAlign: 'center',
                fontWeight: '400',
                width: '100%',
                fontSize: '48px',
                fontFamily: 'Work Sans',
              }}
            >
              Customized Just For You!
            </Typography>
          </>
        )}

        <Grid item display={'flex'} flexDirection={'row'} justifyContent={'center'}>
          {!!props.isUpdateMode ? (
            <>
              <Button
                variant="outlined"
                sx={{
                  height: '40px',
                  width: '300px',
                  fontWeight: '800',
                  color: '#39A2DB',
                  border: '2px solid',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  '&:hover': { background: '#fff' },
                }}
                onClick={handleClickUpdatePlan}
                disabled={!!props.disabledUpdateButton}
              >
                {!!props.actionButtonText?.length ? props.actionButtonText : 'Update Now'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                sx={{
                  height: '40px',
                  width: '300px',
                  fontWeight: '800',
                  background: props.selectedGuid === props.contract?.Uuid || !props.contract.Uuid ? '#39A2DB' : '#fff',
                  color: props.selectedGuid === props.contract?.Uuid || !props.contract.Uuid ? '#fff' : '#39A2DB',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  '&:hover': { background: '#2F84B2' },
                }}
                onClick={handleClickSelectGuid}
              >
                {props.selectedGuid === props.contract.Uuid || !props.contract.Uuid ? 'Selected' : 'Select'}
              </Button>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

function DebitCardPricing(props: { tiers?: AmountTier[]; setShowPricingTable?: React.Dispatch<React.SetStateAction<boolean>>; amount?: number }) {
  return !!props.tiers ? (
    <>
      <Stack justifyContent={'space-between'} direction={'column'} sx={{ width: '130px', height: '80px', border: '1px solid #eeeeee', borderRadius: '6px', padding: '5px 0', boxSizing: 'border-box' }}>
        <Typography sx={{ lineHeight: '20px' }}>Debit Card</Typography>
        <Typography sx={{ lineHeight: '16px' }}>
          <NumberFormat value={+props.tiers[0].Amount / 100} prefix="$" decimalSeparator="." displayType="text" type="text" decimalScale={2} fixedDecimalScale={true} />
          &nbsp;up to&nbsp;
          <NumberFormat value={+props.tiers[1].Floor / 100} prefix="$" decimalSeparator="." displayType="text" type="text" decimalScale={0} fixedDecimalScale={true} />
        </Typography>
        <Link
          onMouseOver={(event: React.SyntheticEvent) => {
            !!props.setShowPricingTable && props.setShowPricingTable(true);
          }}
          onMouseLeave={(event: React.SyntheticEvent) => {
            !!props.setShowPricingTable && props.setShowPricingTable(false);
          }}
          underline="none"
          sx={{ lineHeight: '16px', color: '#39A2DB !important' }}
        >
          {'& more..'}
        </Link>
      </Stack>
    </>
  ) : !!props.amount ? (
    <>
      <Stack justifyContent={'space-between'} direction={'column'} sx={{ width: '130px', height: '80px', border: '1px solid #eeeeee', borderRadius: '6px', padding: '5px 0', boxSizing: 'border-box' }}>
        {/* DB Text */}
        <Typography sx={{ lineHeight: '20px' }}>Debit Card</Typography>
        <Typography sx={{ lineHeight: '16px' }}>
          <NumberFormat value={+props.amount / 10} prefix="$" decimalSeparator="." displayType="text" type="text" decimalScale={2} fixedDecimalScale={true} />
        </Typography>
      </Stack>
    </>
  ) : (
    <></>
  );
}

function ACHPricing(props: { amount: number }) {
  return (
    <Stack justifyContent={'space-between'} direction={'column'} sx={{ width: '70px', height: '80px', border: '1px solid #eeeeee', borderRadius: '6px', padding: '5px 0', boxSizing: 'border-box' }}>
      {/* ACH Text */}
      <Typography sx={{ lineHeight: '20px' }}>ACH</Typography>
      <Typography sx={{ lineHeight: '16px' }}>
        <NumberFormat value={+props.amount / 100} prefix="$" decimalSeparator="." displayType="text" type="text" decimalScale={2} fixedDecimalScale={true} />
      </Typography>
    </Stack>
  );
}

function CreditCardPricing(props: { amount: number }) {
  return (
    <Stack justifyContent={'space-between'} direction={'column'} sx={{ width: '100px', height: '80px', border: '1px solid #eeeeee', borderRadius: '6px', padding: '5px 0', boxSizing: 'border-box' }}>
      <Typography sx={{ lineHeight: '20px' }}>Credit Card</Typography>
      <Typography sx={{ lineHeight: '16px' }}>{+props.amount / 10}%</Typography>
    </Stack>
  );
}

function RowOfFees(props: {
  setShowPricingTable?: React.Dispatch<React.SetStateAction<boolean>>;
  tiers?: AmountTier[];
  dbPricing?: number;
  achPricing: number;
  ccPricing: number;
  isPartial?: boolean;
}) {
  return (
    <Grid
      container
      columnGap={1}
      justifyContent={'center'}
      flexDirection={'row'}
      sx={{ height: '80px', margin: '2% 0', textAlign: 'center', '.MuiTypography-root': { fontFamily: 'Work Sans', fontSize: '14px', color: '#6B7280' } }}
    >
      <Grid item>{!props.isPartial && <ACHPricing amount={props.achPricing} />}</Grid>
      <Grid item>
        <DebitCardPricing tiers={props.tiers} setShowPricingTable={props.setShowPricingTable} amount={props.dbPricing} />
      </Grid>
      <Grid item>
        <CreditCardPricing amount={props.ccPricing} />
      </Grid>
    </Grid>
  );
}

export function TieredPricingTable(props: TieredPricingTablePropTypes) {
  const parseTiers = (tiers: AmountTier[]): { fee: number; feeType: TieredFeeTypeEnum; low: number; high?: number }[] => {
    return tiers?.map((tier, index) => {
      return { fee: tier.Amount, low: tier.Floor === 0 ? 0 : tier.Floor + 1, high: tiers?.[index + 1]?.Floor, feeType: tier.FeeType };
    });
  };
  const parsedTiers = parseTiers(props.tiers);

  return (
    <TableContainer
      sx={{
        '.MuiTableCell-root': {
          padding: '1% 0% 1% 6%',
          textAlign: 'left',
          borderLeft: '1px solid #eeeeee',
        },
        width: '100%',
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '60px', textAlign: 'center !important', paddingLeft: '2% !important', paddingRight: '2% !important', boxSizing: 'border-box' }}>
              Fee
              {PaymentTypeEnum.Db === props.PaymentType && (
                <>
                  <br />
                  <sub>As low as</sub>
                </>
              )}
            </TableCell>
            <TableCell sx={{ width: '160px', paddingLeft: '15% !important' }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parsedTiers?.map((parsedTier, index) => {
            return (
              <TableRow key={index} sx={{ lineHeight: '20px', fontSize: '14px', '.MuiTableCell-root': { borderBottom: 'none' } }}>
                <TableCell sx={{ paddingLeft: '2% !important', paddingRight: '2% !important', textAlign: 'center !important' }}>
                  {parsedTier.feeType === TieredFeeTypeEnum.P ? `${(+parsedTier.fee / 10).toFixed(1)}%` : `$${(+parsedTier.fee / 100).toFixed(2)}`}
                </TableCell>
                <TableCell>
                  {parsedTier.low === 0 ? `up to $${(+(parsedTier.high ?? 0) / 100).toFixed(0)}` : `$${+parsedTier.low / 100}${!parsedTier.high ? '+' : ` - ${(+parsedTier.high / 100).toFixed(2)}`}`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
